
import React from 'react';
import { motion } from 'framer-motion';
import { Form } from '@/components/ui/form';
import FormInput from './FormComponents/FormInput';
import MessageField from './FormComponents/MessageField';
import SubmitButton from './FormComponents/SubmitButton';
import { useContactForm } from './FormComponents/useContactForm';

const ContactForm = () => {
  const { form, isSubmitting, submissionSuccess, onSubmit } = useContactForm();
  const nameInputRef = React.useRef<HTMLInputElement>(null);
  const messageTextareaRef = React.useRef<HTMLTextAreaElement>(null);
  const maxChars = 500;

  // Auto-focus on name field when component mounts
  React.useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  // Animation variants
  const formVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut" 
      }
    }
  };

  return (
    <motion.div
      variants={formVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      className="max-w-4xl mx-auto"
    >
      <h3 className="text-white text-xl font-medium mb-6">Send us a message</h3>
      
      <Form {...form}>
        <form 
          onSubmit={form.handleSubmit(onSubmit)} 
          className="space-y-5"
          aria-label="Contact form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <motion.div variants={itemVariants}>
              <FormInput
                control={form.control}
                name="name"
                label="Name"
                placeholder="Your name"
                inputRef={nameInputRef}
                required={true}
              />
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <FormInput
                control={form.control}
                name="email"
                label="Email"
                placeholder="Your email"
                type="email"
                required={true}
              />
            </motion.div>
          </div>
          
          {/* Optional fields with progressive disclosure pattern */}
          <motion.div variants={itemVariants}>
            <FormInput
              control={form.control}
              name="phone"
              label="Phone"
              placeholder="Your phone number"
              type="tel"
              required={false}
              isOptional={true}
            />
          </motion.div>

          <motion.div variants={itemVariants}>
            <FormInput
              control={form.control}
              name="business"
              label="Business"
              placeholder="Your business or organization"
              required={false}
              isOptional={true}
            />
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <MessageField 
              control={form.control} 
              maxChars={maxChars} 
              messageRef={messageTextareaRef}
            />
          </motion.div>
          
          <motion.div variants={itemVariants} className="pt-2">
            <SubmitButton 
              isSubmitting={isSubmitting} 
              submissionSuccess={submissionSuccess} 
            />
          </motion.div>
        </form>
      </Form>
    </motion.div>
  );
};

export default ContactForm;
